import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowRightIcon from "@tCentro/Icons/ArrowRightIcon";
import { TYPOGRAPHY } from "@tCentro/theme/design";

import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    videoURL = "videoURL",
    mainTitle = "mainTitle",
    title = "title",
    titleLink = "titleLink",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.videoURL]: string;
        [fields.titleLink]: string;
        [fields.mainTitle]: string;
        [fields.links]: LinkModel[];
    };
}

export interface CENPremierBrandSquareVideosStyles {
    maincontainerStyles: GridContainerProps;
    mainTitleStyles: InjectableCss;
    mainTitleGridItemStyles: GridItemProps;
    fullwidthvideoGridItemStyles: GridItemProps;
    halfwidthvideoGridItemStyles: GridItemProps;
    halfwidthlinkcontainerStyles: GridItemProps;
    fullwidthlinkcontainerStyles: GridItemProps;
    linkStyles: InjectableCss;
    linkGridItemStyles: GridItemProps;
    arrowIconStyles: IconPresentationProps;
    containerStyles: InjectableCss;
    contentContainer: GridContainerProps;
    halfwidthlinkItemStyles: GridItemProps;
    fullwidthlinkItemStyles: GridItemProps;
}

export const cenPremierBrandSquareVideosStyles: CENPremierBrandSquareVideosStyles = {
    maincontainerStyles: {
        gap: 24,
        css: css`
            margin-top: 96px;
            width: 90%;
            margin-left: auto;
            margin-right: auto;
            @media (min-width: 992px) and (max-width: 1200px) {
                width: 97%;
            }
            > div[class^="GridOffset"] {
                width: calc(100% + 12px);
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: calc(100% + 14px);
                        `,
                        css`
                            width: calc(100% + 14px);
                        `,
                        css`
                            width: 100%;
                        `,
                        null,
                        null,
                    ])}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 56px;
                        width: 100%;
                    `,
                    css`
                        margin-top: 56px;
                        width: 97%;
                    `,
                    css`
                        margin-top: 56px;
                        padding: 0px;
                        width: 97%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    mainTitleStyles: {
        css: css`
            font-size: ${TYPOGRAPHY.size.xxlarge};
            font-weight: ${TYPOGRAPHY.weight.bold};
            line-height: 40px;
            color: ${getProp("theme.typography.p.color")};
            border-bottom: 2px solid ${getColor("primary")};
            padding-bottom: 33px;
        `,
    },
    fullwidthvideoGridItemStyles: {
        width: [12, 12, 12, 12, 12],
    },

    halfwidthvideoGridItemStyles: {
        width: [12, 12, 6, 4, 4],
    },
    halfwidthlinkcontainerStyles: {
        width: [12, 12, 6, 8, 8],
        css: css`
            border: 4px solid #f2f2f2;
            padding: 40px;
            padding-top: 0px;
            margin: 12px 0px !important;
            align-items: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0px 12px !important;
                        padding-top: 40px;
                    `,
                    css`
                        margin: 0px 12px !important;
                        padding-top: 40px;
                    `,
                    css`
                        padding: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    fullwidthlinkcontainerStyles: {
        width: [12, 12, 12, 12, 12],
        css: css`
            border: 4px solid #f2f2f2;
            padding: 40px 40px 40px 104px;
            margin: 104px 0px 0px 12px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 40px;
                        margin-top: 56px !important;
                    `,
                    css`
                        padding: 40px;
                        margin-top: 56px !important;
                    `,
                    css`
                        padding: 40px;
                        margin-top: 56px !important;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    linkStyles: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            line-height: 16px;
            margin-right: 15px;
        `,
    },
    linkGridItemStyles: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0;
            padding-top: 17px;
            ${GridWrapper} {
                ${GridOffset} {
                    width: 100%;
                }
            }
        `,
    },
    mainTitleGridItemStyles: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding: 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-left: 15px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    containerStyles: {
        css: css`
            align-items: center;
            display: flex;
            margin-top: 24px;
        `,
    },
    arrowIconStyles: {
        src: ArrowRightIcon,
        css: css`
            svg {
                width: 12px;
                > path {
                    fill: #0f96d9;
                }
            }
        `,
    },

    contentContainer: {
        css: css`
            ${GridOffset} {
                margin: 0px;
                justify-content: flex-start;
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css``,
                        css``,
                        css`
                            justify-content: space-around;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    halfwidthlinkItemStyles: {
        width: [12, 12, 6, 4, 4],
        css: css`
            padding: 0px;
        `,
    },
    fullwidthlinkItemStyles: {
        width: [12, 12, 5, 4, 4],
        css: css`
            padding: 0px;
            > div {
                justify-content: flex-start;
                > a {
                    width: 80%;
                    margin-right: 10px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) => breakpointMediaQueries(theme, [css``, css``, css``, css``, null])}
        `,
    },
};

const styles = cenPremierBrandSquareVideosStyles;

const CENPremierBrandSquareVideos: FC<OwnProps> = ({ fields }) => {
    const { links } = useGetLinks(fields.links, o => o.fields.destination);
    return (
        <GridContainer {...styles.maincontainerStyles}>
            {fields.videoURL !== "" && (
                <GridItem
                    {...(fields.links.length > 6
                        ? { ...styles.fullwidthvideoGridItemStyles }
                        : { ...styles.halfwidthvideoGridItemStyles })}
                >
                    <iframe width="100%" height="340px" src={fields.videoURL} frameBorder="0"></iframe>
                </GridItem>
            )}
            <GridItem
                {...(fields.links.length > 6 || fields.videoURL === ""
                    ? { ...styles.fullwidthlinkcontainerStyles }
                    : { ...styles.halfwidthlinkcontainerStyles })}
            >
                <GridContainer {...styles.contentContainer}>
                    <GridItem {...styles.mainTitleGridItemStyles}>
                        <Typography {...styles.mainTitleStyles}>{fields.mainTitle}</Typography>
                    </GridItem>

                    <GridItem {...styles.linkGridItemStyles}>
                        <GridContainer>
                            {links.map((link, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <GridItem
                                    key={index}
                                    {...(fields.links.length > 6 || fields.videoURL === ""
                                        ? { ...styles.halfwidthlinkItemStyles }
                                        : { ...styles.fullwidthlinkItemStyles })}
                                >
                                    {link.url && (
                                        <StyledWrapper {...styles.containerStyles}>
                                            <Link
                                                href={link.url}
                                                target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                                {...styles.linkStyles}
                                            >
                                                {fields.links[index].fields.overriddenTitle || link.title}
                                            </Link>
                                            <Icon src={ArrowRightIcon} {...styles.arrowIconStyles} />
                                        </StyledWrapper>
                                    )}
                                </GridItem>
                            ))}
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: CENPremierBrandSquareVideos,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        displayName: "Premier Brand Square Videos",
        fieldDefinitions: [
            {
                name: fields.mainTitle,
                editorTemplate: "TextField",
                displayName: "Resources Title",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.videoURL,
                editorTemplate: "TextField",
                displayName: "Video URL",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Link Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
